import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { App, Empty, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
    AppApc,
    type CircleDetail,
    type CirclePerson,
    type CreateRedPacketPayload,
    type RedPacket,
    type RedPacketListStatus,
    type RedPacketTemplate,
} from '../../api/appApc';
import { getApiErrorMessage, useAuth } from '../../auth/AuthContext';
import { formatDateTime, formatNumber } from '../../i18n/format';
import { debitAvailableCredits, readWalletAmounts } from '../Wallet/walletData';
import '../Home/Home.css';
import './RedPacket.css';

const TEMPLATES = [
    { id: 'classic', name: 'Classic red', category: 'Everyday', symbol: '✧', tagline: 'A little good fortune', message: 'A little luck, just for you.' },
    { id: 'mint', name: 'APC mint', category: 'Everyday', symbol: '+', tagline: 'Good things, shared', message: 'Good things are better shared.' },
    { id: 'night', name: 'Midnight', category: 'Everyday', symbol: '✦', tagline: 'A bright little surprise', message: 'A little surprise to brighten your day.' },
    { id: 'birthday', name: 'Birthday pop', category: 'Birthday', symbol: '◉', tagline: 'Make a wish', message: 'Hope your year is full of good surprises.' },
    { id: 'lunar', name: 'Lunar New Year', category: 'New Year', symbol: '✺', tagline: 'A prosperous new year', message: 'Wishing you joy, prosperity, and good fortune in the year ahead!' },
    { id: 'thanks', name: 'With thanks', category: 'Thank-you', symbol: '♥', tagline: 'With appreciation', message: 'A little thank-you for making a big difference.' },
    { id: 'team', name: 'Team cheers', category: 'Team', symbol: '✦', tagline: 'Better together', message: 'Great work, team — this one’s for all of you.' },
] as const;

const EXPIRY_OPTIONS = ['1 hour', '24 hours', '3 days', '7 days'] as const;
const EXPIRY_HOURS = { '1 hour': 1, '24 hours': 24, '3 days': 72, '7 days': 168 } as const;
const TEMPLATE_TO_API = {
    classic: 'CLASSIC',
    mint: 'APC_MINT',
    night: 'MIDNIGHT',
    birthday: 'BIRTHDAY',
    lunar: 'LUNAR_NEW_YEAR',
    thanks: 'THANK_YOU',
    team: 'TEAM_CHEERS',
} as const satisfies Record<string, RedPacketTemplate>;
const API_TO_TEMPLATE = Object.fromEntries(
    Object.entries(TEMPLATE_TO_API).map(([id, code]) => [code, id])
) as Record<RedPacketTemplate, TemplateId>;
const DEADLINE_OPTIONS = ['24 hours', '3 days', '7 days', '14 days'] as const;
function sanitizeDecimal(value: string) {
    const cleaned = value.replace(/[^\d.]/g, '');
    const dot = cleaned.indexOf('.');
    const whole = (dot === -1 ? cleaned : cleaned.slice(0, dot)).replace(/^0+(?=\d)/, '');
    if (dot === -1) return whole;
    const fraction = cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
    return `${whole || '0'}.${fraction}`;
}

function sanitizeInteger(value: string, max?: number) {
    const digits = value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    if (!digits) return 0;
    const next = Number(digits);
    return max == null ? next : Math.min(max, next);
}

const GROUP_GIFT_ENABLED = false;

const TABS = [
    ['create', 'Create Red Packet'],
    ['packets', 'My packets'],
    ['circles', 'Circles'],
    ['groups', 'Group gifts'],
    ['receive', 'Recipient preview'],
] as const;

type TemplateId = (typeof TEMPLATES)[number]['id'];
type Tab = (typeof TABS)[number][0];
type Expiry = (typeof EXPIRY_OPTIONS)[number];
type Deadline = (typeof DEADLINE_OPTIONS)[number];
type Audience = 'open' | 'circle';
type ShareMode = 'Random' | 'Equal';
type Privacy = 'shared' | 'private';
type MemberStatus = 'Owner' | 'Active' | 'Pending';
type GroupStatus = 'Draft' | 'Funding' | 'Goal reached' | 'Settling' | 'Completed';
type GroupPolicy = 'All or nothing' | 'Send collected';

type Member = { id?: string; userId?: string; name: string; initials: string; status: MemberStatus; self?: boolean };
type Circle = {
    id: string;
    name: string;
    privacy: Privacy;
    isOwner: boolean;
    description: string;
    eligibleCount: number;
    pendingCount: number;
    members: Member[];
};
type Contribution = { name: string; initials: string; amount: number };
type Dialog =
    | { kind: 'review' }
    | { kind: 'ready'; url: string }
    | { kind: 'group-review' }
    | { kind: 'new-circle' }
    | { kind: 'invite'; circleId: string }
    | null;

const CONTRIBUTION_SAMPLES: Contribution[] = [
    { name: 'Priya', initials: 'PR', amount: 75 },
    { name: 'Leo', initials: 'LE', amount: 125 },
    { name: 'Grace', initials: 'GR', amount: 100 },
];

function money(value: number) {
    const whole = Math.round(value * 100) % 100 === 0;
    const digits = whole ? 0 : 2;
    return formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function templateById(id: string) {
    return TEMPLATES.find((item) => item.id === id) ?? TEMPLATES[0];
}

function toCents(value: number) {
    return Math.round(value * 100);
}

function packetFee(value: number) {
    return Math.round(toCents(value) * 0.03) / 100;
}

function shareStatusParam(status: string) {
    if (status === 'EXPIRED' || status === 'CANCELLED' || status === 'FAILED') return 'expired';
    if (status === 'FULLY_CLAIMED') return 'fullclaim';
    return 'active';
}

function packetShareUrl(shareToken: string, status = 'active') {
    const url = new URL('/red-packet', window.location.origin);
    url.searchParams.set('token', shareToken);
    url.searchParams.set('status', status);
    return url.toString();
}

function createIdempotencyKey() {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return `pkt_${Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('')}`;
}

function toCircle(detail: CircleDetail): Circle {
    return {
        id: detail.id,
        name: detail.name,
        privacy: detail.isShared ? 'shared' : 'private',
        isOwner: detail.isOwner,
        description: '',
        eligibleCount: detail.eligibleCount,
        pendingCount: detail.pendingCount,
        members: (detail.members ?? []).map((member) => ({
            id: member.id,
            userId: member.userId,
            name: member.displayName,
            initials: initialsOf(member.displayName),
            status: member.status === 'PENDING' ? 'Pending' : 'Active',
        })),
    };
}

function statusKey(status: string) {
    if (status === 'ACTIVE') return 'Active';
    if (status === 'FULLY_CLAIMED') return 'Fully claimed';
    if (status === 'EXPIRED') return 'Expired';
    if (status === 'FUNDING') return 'Funding';
    if (status === 'CANCELLED') return 'Cancelled';
    if (status === 'FAILED') return 'Failed';
    return status;
}

function initialsOf(value: string) {
    return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'AP';
}

function eligibleRecipients(circle: Circle) {
    return circle.members.filter((member) => member.status === 'Active' && !member.self);
}

function activeMembers(circle: Circle) {
    return circle.members.filter((member) => member.status === 'Active' || member.status === 'Owner').length;
}

function RedPacketEmpty({ title, description }: { title?: string; description: string }) {
    return (
        <div className="rp-empty">
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={title ? (
                    <div>
                        <strong>{title}</strong>
                        <p>{description}</p>
                    </div>
                ) : description}
            />
        </div>
    );
}

function PacketSelect({
    id,
    value,
    onChange,
    options,
    ariaLabel,
}: {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: string }[];
    ariaLabel?: string;
}) {
    return (
        <Select
            id={id}
            className="rp-select"
            popupClassName="rp-select-dropdown"
            value={value}
            aria-label={ariaLabel}
            suffixIcon={<DownOutlined />}
            popupMatchSelectWidth
            onChange={onChange}
            options={options}
        />
    );
}

function Envelope({ templateId, opening = false, loading = false }: { templateId: string; opening?: boolean; loading?: boolean }) {
    const { t } = useTranslation('redPacket');
    const template = templateById(templateId);
    const name = t(`page.templates.${template.id}.name`);
    return (
        <div className={`rp-envelope ${template.id}${opening ? ' is-opening' : ''}`} role="img" aria-label={t('page.templateAria', { name, category: t(`page.templates.${template.id}.category`) })}>
            <span className="rp-envelope-flap" aria-hidden />
            <span className="rp-envelope-brand">APC PLUS</span>
            <span className="rp-envelope-seal" aria-hidden>{template.symbol}</span>
            <small>{t(`page.templates.${template.id}.tagline`)}</small>
            {loading ? (
                <span className="rp-envelope-loading" role="status" aria-label={t('open.opening')}>
                    <span className="rp-envelope-spinner" aria-hidden />
                    <span>{t('open.opening')}</span>
                </span>
            ) : null}
        </div>
    );
}

type FireworkBurst = { at: number; x: number; y: number; color: string; count: number };

function Fireworks({ active }: { active: boolean }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (!active) return undefined;
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        if (!canvas || !context) return undefined;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

        const particles: {
            nx: number;
            ny: number;
            vx: number;
            vy: number;
            life: number;
            decay: number;
            size: number;
            color: string;
        }[] = [];
        const bursts: FireworkBurst[] = [
            { at: 60, x: 0.5, y: 0.42, color: '#ffd56a', count: 54 },
            { at: 220, x: 0.32, y: 0.3, color: '#ff5b4a', count: 36 },
            { at: 380, x: 0.68, y: 0.28, color: '#fff4cc', count: 36 },
            { at: 620, x: 0.42, y: 0.22, color: '#ff8f6b', count: 32 },
            { at: 840, x: 0.58, y: 0.34, color: '#ffe08a', count: 40 },
        ];
        const fired = new Set<number>();
        const started = performance.now();
        let frame = 0;
        let cssWidth = 1;
        let cssHeight = 1;
        let ratio = 1;

        const resize = () => {
            const rect = canvas.parentElement?.getBoundingClientRect();
            cssWidth = Math.max(1, rect?.width || canvas.clientWidth || 480);
            cssHeight = Math.max(1, rect?.height || canvas.clientHeight || 560);
            ratio = Math.min(window.devicePixelRatio || 1, 2);
            canvas.style.width = `${cssWidth}px`;
            canvas.style.height = `${cssHeight}px`;
            canvas.width = Math.floor(cssWidth * ratio);
            canvas.height = Math.floor(cssHeight * ratio);
        };
        resize();

        const spawn = (burst: FireworkBurst) => {
            for (let index = 0; index < burst.count; index += 1) {
                const angle = (Math.PI * 2 * index) / burst.count + Math.random() * 0.2;
                const speed = 1.2 + Math.random() * 2.8;
                particles.push({
                    nx: burst.x,
                    ny: burst.y,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    life: 1,
                    decay: 0.008 + Math.random() * 0.008,
                    size: 1.6 + Math.random() * 2.2,
                    color: burst.color,
                });
            }
        };

        const tick = (now: number) => {
            const elapsed = now - started;
            bursts.forEach((burst, index) => {
                if (!fired.has(index) && elapsed >= burst.at) {
                    fired.add(index);
                    spawn(burst);
                }
            });
            context.setTransform(1, 0, 0, 1, 0, 0);
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.setTransform(ratio, 0, 0, ratio, 0, 0);
            particles.forEach((particle) => {
                particle.vy += 0.018;
                particle.vx *= 0.985;
                particle.vy *= 0.985;
                particle.nx += particle.vx / cssWidth;
                particle.ny += particle.vy / cssHeight;
                particle.life -= particle.decay;
            });
            for (let index = particles.length - 1; index >= 0; index -= 1) {
                const particle = particles[index];
                if (particle.life <= 0) {
                    particles.splice(index, 1);
                    continue;
                }
                const x = particle.nx * cssWidth;
                const y = particle.ny * cssHeight;
                context.globalAlpha = Math.max(particle.life, 0);
                context.fillStyle = particle.color;
                context.beginPath();
                context.arc(x, y, particle.size, 0, Math.PI * 2);
                context.fill();
            }
            context.globalAlpha = 1;
            if (particles.length || fired.size < bursts.length) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        const observer = new ResizeObserver(resize);
        if (canvas.parentElement) observer.observe(canvas.parentElement);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            context.setTransform(1, 0, 0, 1, 0, 0);
            context.clearRect(0, 0, canvas.width, canvas.height);
        };
    }, [active]);

    return <canvas ref={canvasRef} className={`rp-fireworks${active ? ' is-active' : ''}`} aria-hidden />;
}

function RedPacketPage() {
    const { t } = useTranslation('redPacket');
    const { message } = App.useApp();
    const { user, refreshProfile, commitUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const wallet = readWalletAmounts(user);
    const sender = (user?.displayName || '').split(' ')[0] || 'APC';

    const [tab, setTab] = useState<Tab>('create');
    const [amountText, setAmountText] = useState('0');
    const amount = Number(amountText) || 0;
    const fee = packetFee(amount);
    const debit = Math.round((amount + fee) * 100) / 100;
    const [recipients, setRecipients] = useState(0);
    const [openRecipients, setOpenRecipients] = useState(0);
    const [audience, setAudience] = useState<Audience>('open');
    const [selectedCircle, setSelectedCircle] = useState('');
    const [mode, setMode] = useState<ShareMode>('Random');
    const [expiry, setExpiry] = useState<Expiry>('24 hours');
    const [template, setTemplate] = useState<TemplateId>('classic');
    const [wish, setWish] = useState<string>(TEMPLATES[0].message);
    const [wishTouched, setWishTouched] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [packets, setPackets] = useState<RedPacket[]>([]);
    const [circles, setCircles] = useState<Circle[]>([]);
    const [loadingPage, setLoadingPage] = useState(true);
    const [dialog, setDialog] = useState<Dialog>(null);
    const [circleName, setCircleName] = useState('');
    const [circlePrivacy, setCirclePrivacy] = useState<Privacy>('shared');
    const [peopleQuery, setPeopleQuery] = useState('');
    const [people, setPeople] = useState<CirclePerson[]>([]);
    const [peopleReady, setPeopleReady] = useState(false);
    const [pickedPeople, setPickedPeople] = useState<CirclePerson[]>([]);
    const [inboxStatus, setInboxStatus] = useState<RedPacketListStatus>('openable');
    const [inboxPackets, setInboxPackets] = useState<RedPacket[]>([]);
    const [linkPacket, setLinkPacket] = useState<RedPacket | null>(null);
    const [receiveLoading, setReceiveLoading] = useState(false);
    const balanceTimer = useRef<number | undefined>(undefined);
    const pendingDebit = useRef<{ before: number; debit: number } | null>(null);

    const [groupCreated, setGroupCreated] = useState(false);
    const [groupTitle, setGroupTitle] = useState('Celebrate Maya');
    const [groupRecipient, setGroupRecipient] = useState('maya@example.com');
    const [groupTarget, setGroupTarget] = useState(300);
    const [groupDeadline, setGroupDeadline] = useState<Deadline>('7 days');
    const [groupPolicy, setGroupPolicy] = useState<GroupPolicy>('All or nothing');
    const [groupAudience, setGroupAudience] = useState('circle:design-team');
    const [groupTemplate, setGroupTemplate] = useState<TemplateId>('team');
    const [groupStatus, setGroupStatus] = useState<GroupStatus>('Draft');
    const [contributions, setContributions] = useState<Contribution[]>([]);
    const [groupError, setGroupError] = useState('');

    const available = Math.max(0, wallet.availableCredits);
    const ownedCircles = circles.filter((item) => item.isOwner);
    const circle = ownedCircles.find((item) => item.id === selectedCircle) ?? ownedCircles[0];
    const circleNameOf = (item?: Circle) => (item ? t(`page.circleItems.${item.id}.name`, { defaultValue: item.name }) : t('page.savedCircle'));
    const audienceLabel = audience === 'open' ? t('page.anyone') : circleNameOf(circle);
    const templateName = (id: string) => t(`page.templates.${id}.name`);
    const templateCategory = (id: string) => t(`page.templates.${id}.category`);
    const expiryLabel = (value: string) => t(`open.expiry.${value}`);
    const modeLabel = (value: string) => t(`page.modes.${value}`);
    const statusLabel = (value: string) => t(`page.status.${value}`, { defaultValue: value });
    const policyLabel = (value: string) => t(`page.policies.${value}`, { defaultValue: value });
    const shownWish = wishTouched ? wish : t(`page.templates.${template}.message`);
    const previewWish = (wishTouched ? wish.trim() : '') || t(`page.templates.${template}.message`);
    const token = searchParams.get('token') || '';

    const selectTemplate = (nextId: TemplateId) => {
        setTemplate(nextId);
    };

    const selectAudience = (next: Audience) => {
        setAudience(next);
        if (next === 'open') {
            setRecipients(openRecipients);
        } else if (circle) {
            setRecipients(eligibleRecipients(circle).length);
        }
    };

    const selectCircle = (id: string) => {
        setSelectedCircle(id);
        const next = circles.find((item) => item.id === id);
        if (next) setRecipients(eligibleRecipients(next).length);
    };

    const loadCircles = useCallback(async () => {
        const list = await AppApc.GetCircles({ limit: 100 });
        const summaries = list.data.data ?? [];
        const details = await Promise.all(summaries.map(async (item) => {
            try {
                const detail = await AppApc.GetCircle(item.id);
                return toCircle(detail.data.data);
            } catch {
                return toCircle({ ...item, members: [] });
            }
        }));
        setCircles(details);
        setSelectedCircle((current) => (
            details.some((item) => item.id === current && item.isOwner)
                ? current
                : details.find((item) => item.isOwner)?.id ?? ''
        ));
    }, []);

    const loadPackets = useCallback(async () => {
        const response = await AppApc.GetRedPackets({ scope: 'mine', limit: 50 });
        setPackets(response.data.data ?? []);
    }, []);

    const loadInboxPackets = useCallback(async () => {
        setReceiveLoading(true);
        try {
            const response = await AppApc.GetRedPackets({ scope: 'inbox', limit: 50 });
            setInboxPackets(Array.isArray(response.data?.data) ? response.data.data : []);
        } catch (loadError) {
            message.error(getApiErrorMessage(loadError, t('page.errors.load')));
        } finally {
            setReceiveLoading(false);
        }
    }, [message, t]);

    const reloadLinkedPacket = useCallback(async (shareToken: string) => {
        setReceiveLoading(true);
        try {
            const response = await AppApc.GetRedPacketByToken(shareToken);
            const packet = response.data.data;
            setLinkPacket(packet);
            if (packet.status === 'EXPIRED') setInboxStatus('expired');
            else if (packet.status === 'FULLY_CLAIMED') setInboxStatus('fully_claimed');
            else setInboxStatus('openable');
        } catch (loadError) {
            message.error(getApiErrorMessage(loadError, t('page.errors.load')));
        } finally {
            setReceiveLoading(false);
        }
    }, [message, t]);

    const selectTab = (next: Tab) => {
        setTab(next);
        if (next === 'packets') {
            void loadPackets().catch((loadError) => {
                message.error(getApiErrorMessage(loadError, t('page.errors.load')));
            });
            void refreshProfile();
            return;
        }
        if (next === 'circles') {
            void loadCircles().catch((loadError) => {
                message.error(getApiErrorMessage(loadError, t('page.errors.load')));
            });
            return;
        }
        if (next === 'create') {
            void refreshProfile();
            void loadCircles().catch((loadError) => {
                message.error(getApiErrorMessage(loadError, t('page.errors.load')));
            });
            return;
        }
        if (next === 'receive') {
            if (token) void reloadLinkedPacket(token);
            else void loadInboxPackets();
        }
    };

    useEffect(() => {
        let cancelled = false;
        setLoadingPage(true);
        Promise.all([loadCircles(), loadPackets()])
            .catch((loadError) => {
                if (!cancelled) message.error(getApiErrorMessage(loadError, t('page.errors.load')));
            })
            .finally(() => {
                if (!cancelled) setLoadingPage(false);
            });
        return () => {
            cancelled = true;
        };
    }, [loadCircles, loadPackets, message, t]);

    useEffect(() => {
        if (!token) {
            setLinkPacket(null);
            return undefined;
        }
        let cancelled = false;
        setTab('receive');
        setReceiveLoading(true);
        AppApc.GetRedPacketByToken(token)
            .then((response) => {
                if (cancelled) return;
                const packet = response.data.data;
                setLinkPacket(packet);
                if (packet.status === 'EXPIRED') setInboxStatus('expired');
                else if (packet.status === 'FULLY_CLAIMED') setInboxStatus('fully_claimed');
                else setInboxStatus('openable');
            })
            .catch((loadError) => {
                if (!cancelled) message.error(getApiErrorMessage(loadError, t('page.errors.load')));
            })
            .finally(() => {
                if (!cancelled) setReceiveLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    useEffect(() => {
        const query = peopleQuery.trim();
        if (query.length < 2 || (dialog?.kind !== 'new-circle' && dialog?.kind !== 'invite')) {
            setPeople([]);
            setPeopleReady(false);
            return undefined;
        }
        setPeopleReady(false);
        const timer = window.setTimeout(() => {
            void AppApc.SearchCirclePeople(query)
                .then((response) => setPeople(response.data.data ?? []))
                .catch(() => setPeople([]))
                .finally(() => setPeopleReady(true));
        }, 300);
        return () => window.clearTimeout(timer);
    }, [peopleQuery, dialog]);

    const reviewPacket = () => {
        const circleCount = circle ? eligibleRecipients(circle).length : 0;
        if (audience === 'circle' && (!circle || circleCount < 1)) {
            setError(t('page.errors.circle'));
            return;
        }
        const receiverCount = audience === 'circle' ? circleCount : recipients;
        const valid = Number.isFinite(amount)
            && toCents(amount) >= 100
            && toCents(debit) <= toCents(available)
            && receiverCount >= 1
            && receiverCount <= 100;
        if (!valid) {
            setError(t('page.errors.amount'));
            return;
        }
        setError('');
        setDialog({ kind: 'review' });
    };

    const syncCreatorBalance = useCallback(async (before: number, debitAmount: number) => {
        const expected = Math.max(0, Math.round((before - debitAmount) * 100) / 100);
        const profile = await refreshProfile(false);
        const source = profile ?? user;
        if (!source || debitAmount <= 0) return;
        const seen = readWalletAmounts(source).availableCredits;
        if (toCents(seen) <= toCents(expected)) {
            if (profile) commitUser(profile);
            return;
        }
        commitUser(debitAvailableCredits(source, seen - expected));
    }, [commitUser, refreshProfile, user]);

    useEffect(() => () => window.clearTimeout(balanceTimer.current), []);

    const confirmPacket = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload: CreateRedPacketPayload = {
                amount: amount.toFixed(2),
                split: mode === 'Equal' ? 'EQUAL' : 'RANDOM',
                idempotencyKey: createIdempotencyKey(),
                template: TEMPLATE_TO_API[template],
                message: previewWish.slice(0, 280),
                expiresInHours: EXPIRY_HOURS[expiry],
                audience: audience === 'circle' ? 'CIRCLE' : 'LINK',
            };
            if (audience === 'circle' && circle) payload.circleId = circle.id;
            else payload.receiverCount = recipients;
            const response = await AppApc.CreateRedPacket(payload);
            const created = response.data.data;
            setPackets((current) => [created, ...current.filter((item) => item.id !== created.id)]);
            setDialog({ kind: 'ready', url: packetShareUrl(created.shareToken) });
            const reserved = Number(created.debitAmount);
            const debitAmount = Number.isFinite(reserved) && reserved > 0 ? reserved : debit;
            pendingDebit.current = { before: available, debit: debitAmount };
            await syncCreatorBalance(available, debitAmount);
            window.clearTimeout(balanceTimer.current);
            balanceTimer.current = window.setTimeout(() => {
                void syncCreatorBalance(available, debitAmount);
            }, 1500);
        } catch (createError) {
            message.error(getApiErrorMessage(createError, t('page.errors.amount')));
        } finally {
            setSubmitting(false);
        }
    };

    const cancelPacket = async (id: string) => {
        try {
            const response = await AppApc.CancelRedPacket({ id });
            const next = response.data.data;
            setPackets((current) => current.map((item) => (item.id === id ? next : item)));
            void refreshProfile();
        } catch (cancelError) {
            message.error(getApiErrorMessage(cancelError, t('page.errors.load')));
        }
    };

    const loadInbox = (status: RedPacketListStatus) => {
        setInboxStatus(status);
        setLinkPacket(null);
        setSearchParams({});
        void loadInboxPackets();
    };

    const receivePackets = useMemo(() => {
        const byId: Record<string, RedPacket> = {};
        packets.concat(inboxPackets).forEach((item) => {
            if (item?.id) byId[item.id] = item;
        });
        return Object.keys(byId).map((id) => byId[id]).filter((item) => {
            if (inboxStatus === 'expired') return item.status === 'EXPIRED';
            if (inboxStatus === 'fully_claimed') return item.status === 'FULLY_CLAIMED';
            return item.status === 'ACTIVE' || item.status === 'FUNDING';
        });
    }, [packets, inboxPackets, inboxStatus]);
    const receivePacket = linkPacket ?? receivePackets[0] ?? null;

    const claimOpenedPacket = async (packet: RedPacket) => {
        const response = await AppApc.ClaimRedPacket({ id: packet.id });
        const next = response.data.data;
        setLinkPacket(next);
        setInboxPackets((current) => current.map((item) => (item.id === next.id ? next : item)));
        setPackets((current) => current.map((item) => (item.id === next.id ? next : item)));
        void refreshProfile();
        return next;
    };

    const copyText = async (value: string, success: string) => {
        try {
            await navigator.clipboard.writeText(value);
            message.success(success);
        } catch {
            message.info(success.replace('copied', 'ready to copy'));
        }
    };

    const funded = contributions.reduce((sum, item) => sum + item.amount, 0);
    const groupAudienceLabel = groupAudience === 'open'
        ? t('page.anyone')
        : circleNameOf(circles.find((item) => item.id === groupAudience.split(':')[1]));

    const reviewGroup = () => {
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(groupRecipient.trim());
        if (!groupTitle.trim() || !emailOk || !Number.isFinite(groupTarget) || groupTarget < 1) {
            setGroupError(t('page.errors.group'));
            return;
        }
        setGroupError('');
        setDialog({ kind: 'group-review' });
    };

    const closeDialog = () => {
        if (dialog?.kind === 'ready' && pendingDebit.current) {
            const pending = pendingDebit.current;
            void syncCreatorBalance(pending.before, pending.debit);
        }
        setDialog(null);
    };

    return (
        <div className="red-packet">
            <div className="page-heading">
                <div>
                    <p className="rp-eyebrow">{t('page.eyebrow')}</p>
                    <h1>{t('page.title')}</h1>
                    <p>{t('page.subtitle')}</p>
                </div>
                <span className="rp-large-status">{t('page.available')}</span>
            </div>

            <div className="rp-shell">
                <div className="rp-summary-bar">
                    <div>
                        <small>{t('page.balanceLabel')}</small>
                        <strong>{t('page.balanceAmount', { amount: money(available) })}</strong>
                    </div>
                    <p>{t('page.intro')}</p>
                </div>

                <nav className="rp-tabs" aria-label={t('page.tabsAria')}>
                    {TABS.map(([id]) => (
                        <button
                            key={id}
                            type="button"
                            className={tab === id ? 'is-active' : ''}
                            aria-current={tab === id ? 'page' : undefined}
                            onClick={() => selectTab(id)}
                        >
                            {t(`page.tabs.${id}`)}
                        </button>
                    ))}
                </nav>

                {tab === 'create' ? (
                    <div className="rp-create-grid">
                        <section className="rp-panel rp-form-panel">
                            <div className="rp-panel-head">
                                <h2>{t('page.createTitle')}</h2>
                                <p>{t('page.createSubtitle')}</p>
                            </div>
                            <form onSubmit={(event) => { event.preventDefault(); reviewPacket(); }}>
                                <div className="rp-row">
                                    <div className="rp-field">
                                        <label htmlFor="rp-amount">{t('page.amount')}</label>
                                        <div className="rp-input-wrap">
                                            <input
                                                id="rp-amount"
                                                type="text"
                                                inputMode="decimal"
                                                autoComplete="off"
                                                value={amountText}
                                                required
                                                onChange={(event) => setAmountText(sanitizeDecimal(event.target.value))}
                                            />
                                            <span>{t('page.unit')}</span>
                                        </div>
                                        <small>{t('page.amountHint')}</small>
                                    </div>
                                    <div className="rp-field">
                                        <label htmlFor="rp-recipients">{t('page.recipients')}</label>
                                        <div className="rp-input-wrap">
                                            <input
                                                id="rp-recipients"
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete="off"
                                                value={recipients}
                                                readOnly={audience === 'circle'}
                                                required
                                                onChange={(event) => {
                                                    const next = sanitizeInteger(event.target.value, 100);
                                                    setRecipients(next);
                                                    setOpenRecipients(next);
                                                }}
                                            />
                                            <span>{t('page.people')}</span>
                                        </div>
                                        <small>{audience === 'circle' ? t('page.recipientsCircle') : t('page.recipientsOpen')}</small>
                                    </div>
                                </div>

                                <div className="rp-field" role="group" aria-labelledby="rp-audience-label">
                                    <span className="rp-group-label" id="rp-audience-label">{t('page.who')} <small>{audienceLabel}</small></span>
                                    <div className="rp-segment">
                                        <button type="button" className={audience === 'open' ? 'is-selected' : ''} aria-pressed={audience === 'open'} onClick={() => selectAudience('open')}>
                                            <strong>{t('page.linkTitle')}</strong><small>{t('page.linkHint')}</small>
                                        </button>
                                        <button type="button" className={audience === 'circle' ? 'is-selected' : ''} aria-pressed={audience === 'circle'} onClick={() => selectAudience('circle')}>
                                            <strong>{t('page.circleTitle')}</strong><small>{t('page.circleHint')}</small>
                                        </button>
                                    </div>
                                    {audience === 'circle' && ownedCircles.length > 0 ? (
                                        <PacketSelect
                                            id="rp-circle-select"
                                            ariaLabel={t('page.circleAria')}
                                            value={circle?.id || selectedCircle}
                                            onChange={selectCircle}
                                            options={ownedCircles.map((item) => ({
                                                value: item.id,
                                                label: t('page.eligible', { name: circleNameOf(item), count: item.eligibleCount }),
                                            }))}
                                        />
                                    ) : null}
                                    {audience === 'circle' && !loadingPage && ownedCircles.length === 0 ? (
                                        <RedPacketEmpty description={t('page.emptyCircles')} />
                                    ) : null}
                                </div>

                                <div className="rp-field" role="group" aria-labelledby="rp-sharing-label">
                                    <span className="rp-group-label" id="rp-sharing-label">{t('page.shareHow')}</span>
                                    <div className="rp-segment">
                                        <button type="button" className={mode === 'Random' ? 'is-selected' : ''} aria-pressed={mode === 'Random'} onClick={() => setMode('Random')}>
                                            <strong>{t('page.random')}</strong><small>{t('page.randomHint')}</small>
                                        </button>
                                        <button type="button" className={mode === 'Equal' ? 'is-selected' : ''} aria-pressed={mode === 'Equal'} onClick={() => setMode('Equal')}>
                                            <strong>{t('page.equal')}</strong><small>{t('page.equalHint')}</small>
                                        </button>
                                    </div>
                                </div>

                                <div className="rp-field">
                                    <label htmlFor="rp-expiry">{t('page.expires')}</label>
                                    <PacketSelect
                                        id="rp-expiry"
                                        value={expiry}
                                        onChange={(value) => setExpiry(value as Expiry)}
                                        options={EXPIRY_OPTIONS.map((option) => ({ value: option, label: expiryLabel(option) }))}
                                    />
                                    <small>{t('page.expiresHint')}</small>
                                </div>

                                <div className="rp-field" role="group" aria-labelledby="rp-template-label">
                                    <span className="rp-group-label" id="rp-template-label">{t('page.chooseTemplate')} <small>{t('page.templateCount', { count: TEMPLATES.length })}</small></span>
                                    <div className="rp-templates">
                                        {TEMPLATES.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className={`rp-template-card${template === item.id ? ' is-selected' : ''}`}
                                                aria-label={t('page.templateAria', { name: templateName(item.id), category: templateCategory(item.id) })}
                                                aria-pressed={template === item.id}
                                                onClick={() => selectTemplate(item.id)}
                                            >
                                                <span className={`rp-template-art ${item.id}`} aria-hidden>{item.symbol}</span>
                                                <span className="rp-template-copy"><strong>{templateName(item.id)}</strong><small>{templateCategory(item.id)}</small></span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="rp-field">
                                    <label htmlFor="rp-message">{t('page.wishes')} <small>{t('page.wishesMeta')}</small></label>
                                    <input
                                        id="rp-message"
                                        maxLength={80}
                                        value={shownWish}
                                        placeholder={t('page.wishesPlaceholder')}
                                        onChange={(event) => {
                                            setWishTouched(true);
                                            setWish(event.target.value);
                                        }}
                                    />
                                </div>

                                <div className="rp-summary">
                                    <div className="rp-summary-row"><span>{t('page.audience')}</span><strong>{audienceLabel}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.recipients')}</span><strong>{t('page.recipientsLine', { count: recipients, mode: modeLabel(mode) })}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.fee')}</span><strong>{money(fee)}</strong></div>
                                    <div className="rp-summary-row is-total"><span>{t('page.reserve')}</span><strong>{money(debit)}</strong></div>
                                </div>
                                <p className="rp-hint">{t('page.reserveHint')}</p>
                                <p className="rp-error" role="alert">{error}</p>
                                <button className="rp-primary is-full" type="submit">{t('page.review')}</button>
                            </form>
                        </section>

                        <aside className="rp-panel rp-preview-panel">
                            <div className="rp-preview-label"><span>{t('page.preview')}</span><span>{templateCategory(template)}</span></div>
                            <Envelope templateId={template} />
                            <small>{t('page.sentYou', { name: sender })}</small>
                            <h3>{previewWish}</h3>
                            <p>{t('page.openHint')}</p>
                            <div className="rp-preview-facts">
                                <div className="rp-summary-row"><span>{t('page.audience')}</span><strong>{audienceLabel}</strong></div>
                                <div className="rp-summary-row"><span>{t('page.sharing')}</span><strong>{t('page.modeAmounts', { mode: modeLabel(mode) })}</strong></div>
                                <div className="rp-summary-row"><span>{t('page.recipients')}</span><strong>{t('page.peopleCount', { count: recipients })}</strong></div>
                                <div className="rp-summary-row"><span>{t('page.availableFor')}</span><strong>{expiryLabel(expiry)}</strong></div>
                            </div>
                        </aside>
                    </div>
                ) : null}

                {tab === 'packets' ? (
                    <div className="rp-packet-list">
                        <div className="rp-list-head">
                            <h2>{t('page.packetsTitle')}</h2>
                            <button className="rp-primary" type="button" onClick={() => setTab('create')}><PlusOutlined />{t('page.createNew')}</button>
                        </div>
                        {loadingPage ? <p className="rp-hint">{t('page.loading')}</p> : null}
                        {!loadingPage && packets.length === 0 ? <RedPacketEmpty description={t('page.emptyPackets')} /> : null}
                        {packets.map((packet) => {
                            const templateId = API_TO_TEMPLATE[packet.template] ?? 'classic';
                            const item = templateById(templateId);
                            const progress = packet.receiverCount ? Math.round((packet.claimedCount / packet.receiverCount) * 100) : 0;
                            const canCancel = packet.claimedCount === 0 && (packet.status === 'ACTIVE' || packet.status === 'FUNDING');
                            const meta = `${modeLabel(packet.split === 'EQUAL' ? 'Equal' : 'Random')} · ${packet.audience === 'CIRCLE' ? (packet.circleName || t('page.savedCircle')) : t('page.anyone')} · ${formatDateTime(packet.createdAt)}`;
                            const shareUrl = packet.audience === 'LINK' && packet.shareToken && packet.status !== 'FAILED' && packet.status !== 'FUNDING'
                                ? packetShareUrl(packet.shareToken, shareStatusParam(packet.status))
                                : '';
                            return (
                                <article className="rp-panel rp-packet-row" key={packet.id}>
                                    <div className={`rp-packet-thumb ${item.id}`}>{item.symbol}</div>
                                    <div className="rp-packet-info">
                                        <h3>{packet.message || templateName(templateId)}</h3>
                                        <small>{meta}</small>
                                        <div className="rp-progress" role="progressbar" aria-label={t('page.claimProgress')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                                            <i style={{ width: `${progress}%` }} />
                                        </div>
                                        <small>{t('page.claimedOf', { count: packet.claimedCount, total: packet.receiverCount })}</small>
                                        {packet.failureReason ? <small>{packet.failureReason}</small> : null}
                                    </div>
                                    <div className="rp-packet-value">
                                        <strong>{t('page.balanceAmount', { amount: money(Number(packet.totalAmount)) })}</strong>
                                        <span className={`rp-availability${packet.status === 'ACTIVE' ? ' is-live' : ''}`}>{statusLabel(statusKey(packet.status))}</span>
                                        {canCancel ? (
                                            <button className="rp-text-button" type="button" onClick={() => void cancelPacket(packet.id)}>{t('page.cancel')}</button>
                                        ) : null}
                                    </div>
                                    {shareUrl ? (
                                        <div className="rp-packet-share">
                                            <label htmlFor={`rp-share-${packet.id}`}>{t('page.shareLink')}</label>
                                            <div className="rp-share-row">
                                                <input id={`rp-share-${packet.id}`} aria-label={t('page.shareLink')} readOnly value={shareUrl} />
                                                <button className="rp-secondary" type="button" onClick={() => void copyText(shareUrl, t('page.copied'))}>{t('page.copy')}</button>
                                            </div>
                                        </div>
                                    ) : null}
                                </article>
                            );
                        })}
                    </div>
                ) : null}

                {tab === 'circles' ? (
                    <>
                        <section className="rp-feature-intro">
                            <div>
                                <span className="rp-feature-kicker">{t('page.circles.kicker')}</span>
                                <h2>{t('page.circles.title')}</h2>
                                <p>{t('page.circles.body')}</p>
                            </div>
                            <button className="rp-primary" type="button" onClick={() => { setCircleName(''); setPeopleQuery(''); setPeople([]); setPickedPeople([]); setDialog({ kind: 'new-circle' }); }}><PlusOutlined />{t('page.circles.create')}</button>
                        </section>
                        {!loadingPage && circles.length === 0 ? <RedPacketEmpty description={t('page.emptyCircles')} /> : null}
                        <div className="rp-circle-grid">
                            {circles.map((item) => {
                                const eligible = item.eligibleCount;
                                const pending = item.pendingCount;
                                return (
                                    <article className="rp-panel rp-circle-card" key={item.id}>
                                        <div className="rp-circle-card-head">
                                            <div className="rp-circle-mark">{initialsOf(item.name)}</div>
                                            <div>
                                                <div className="rp-circle-title">
                                                    <h3>{circleNameOf(item)}</h3>
                                                    <span>{item.privacy === 'shared' ? t('page.circles.shared') : t('page.circles.private')}</span>
                                                </div>
                                                {item.description ? <p>{item.description}</p> : null}
                                            </div>
                                        </div>
                                        <div className="rp-avatar-stack" aria-label={t('page.circles.activePeople', { count: activeMembers(item) })}>
                                            {item.members.slice(0, 5).map((member) => (
                                                <span key={`${item.id}-${member.name}`} className={`rp-member-avatar${member.status === 'Pending' ? ' is-pending' : ''}`} title={`${member.name} · ${t(`page.member.${member.status}`)}`}>{member.initials}</span>
                                            ))}
                                        </div>
                                        <div className="rp-circle-facts">
                                            <span>{t('page.circles.active', { count: activeMembers(item) })}</span>
                                            <span>{t('page.circles.eligible', { count: eligible })}</span>
                                            {pending ? <span>{t('page.circles.pending', { count: pending })}</span> : null}
                                        </div>
                                        <div className="rp-circle-actions">
                                            {item.isOwner ? (
                                                <button className="rp-secondary" type="button" onClick={() => { setPeopleQuery(''); setPeople([]); setPickedPeople([]); setDialog({ kind: 'invite', circleId: item.id }); }}>
                                                    {item.privacy === 'shared' ? t('page.circles.invite') : t('page.circles.addPerson')}
                                                </button>
                                            ) : null}
                                            <button
                                                className="rp-primary"
                                                type="button"
                                                disabled={!item.isOwner || eligible < 1}
                                                onClick={() => {
                                                    setSelectedCircle(item.id);
                                                    setAudience('circle');
                                                    setRecipients(eligible);
                                                    setTab('create');
                                                }}
                                            >
                                                {t('page.circles.use')}
                                            </button>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                        <div className="rp-principle">
                            <span>◎</span>
                            <div>
                                <strong>{t('page.circles.principleTitle')}</strong>
                                <p>{t('page.circles.principleBody')}</p>
                            </div>
                        </div>
                    </>
                ) : null}

                {tab === 'groups' && !GROUP_GIFT_ENABLED ? (
                    <section className="rp-panel rp-coming-soon">
                        <RedPacketEmpty title={t('page.groups.comingSoon')} description={t('page.groups.comingSoonBody')} />
                    </section>
                ) : null}

                {tab === 'groups' && GROUP_GIFT_ENABLED && !groupCreated ? (
                    <div className="rp-group-grid">
                        <section className="rp-panel rp-form-panel">
                            <div className="rp-panel-head">
                                <span className="rp-feature-kicker">{t('page.groups.kicker')}</span>
                                <h2>{t('page.groups.title')}</h2>
                                <p>{t('page.groups.body')}</p>
                            </div>
                            <form onSubmit={(event) => { event.preventDefault(); reviewGroup(); }}>
                                <div className="rp-field">
                                    <label htmlFor="rp-group-title">{t('page.groups.name')}</label>
                                    <input id="rp-group-title" maxLength={60} value={groupTitle} required onChange={(event) => setGroupTitle(event.target.value)} />
                                </div>
                                <div className="rp-field">
                                    <label htmlFor="rp-group-recipient">{t('page.groups.email')}</label>
                                    <input id="rp-group-recipient" type="email" value={groupRecipient} required onChange={(event) => setGroupRecipient(event.target.value)} />
                                    <small>{t('page.groups.emailHint')}</small>
                                </div>
                                <div className="rp-row">
                                    <div className="rp-field">
                                        <label htmlFor="rp-group-target">{t('page.groups.target')}</label>
                                        <div className="rp-input-wrap">
                                            <input
                                                id="rp-group-target"
                                                type="text"
                                                inputMode="numeric"
                                                autoComplete="off"
                                                value={groupTarget}
                                                required
                                                onChange={(event) => setGroupTarget(sanitizeInteger(event.target.value))}
                                            />
                                            <span>{t('page.unit')}</span>
                                        </div>
                                    </div>
                                    <div className="rp-field">
                                        <label htmlFor="rp-group-deadline">{t('page.groups.deadline')}</label>
                                        <PacketSelect
                                            id="rp-group-deadline"
                                            value={groupDeadline}
                                            onChange={(value) => setGroupDeadline(value as Deadline)}
                                            options={DEADLINE_OPTIONS.map((option) => ({ value: option, label: expiryLabel(option) }))}
                                        />
                                    </div>
                                </div>
                                <div className="rp-field">
                                    <label htmlFor="rp-group-policy">{t('page.groups.policy')}</label>
                                    <PacketSelect
                                        id="rp-group-policy"
                                        value={groupPolicy}
                                        onChange={(value) => setGroupPolicy(value as GroupPolicy)}
                                        options={[
                                            { value: 'All or nothing', label: policyLabel('All or nothing') },
                                            { value: 'Send collected', label: policyLabel('Send collected') },
                                        ]}
                                    />
                                    <small>{t('page.groups.policyHint')}</small>
                                </div>
                                <div className="rp-field">
                                    <label htmlFor="rp-group-audience">{t('page.groups.who')}</label>
                                    <PacketSelect
                                        id="rp-group-audience"
                                        value={groupAudience}
                                        onChange={setGroupAudience}
                                        options={[
                                            { value: 'open', label: t('page.groups.anyone') },
                                            ...circles.map((item) => ({
                                                value: `circle:${item.id}`,
                                                label: `${circleNameOf(item)} · ${item.privacy === 'shared' ? t('page.circles.shared') : t('page.circles.private')}`,
                                            })),
                                        ]}
                                    />
                                </div>
                                <div className="rp-field">
                                    <label htmlFor="rp-group-template">{t('page.template')}</label>
                                    <PacketSelect
                                        id="rp-group-template"
                                        value={groupTemplate}
                                        onChange={(value) => setGroupTemplate(value as TemplateId)}
                                        options={TEMPLATES.map((item) => ({
                                            value: item.id,
                                            label: `${templateName(item.id)} · ${templateCategory(item.id)}`,
                                        }))}
                                    />
                                </div>
                                <div className="rp-reserve-note">
                                    <span>↳</span>
                                    <div>
                                        <strong>{t('page.groups.noteTitle')}</strong>
                                        <p>{t('page.groups.noteBody')}</p>
                                    </div>
                                </div>
                                <p className="rp-error" role="alert">{groupError}</p>
                                <button className="rp-primary is-full" type="submit">{t('page.groups.review')}</button>
                            </form>
                        </section>
                        <aside>
                            <section className="rp-panel rp-group-preview">
                                <div className="rp-preview-label"><span>{t('page.groups.preview')}</span><span>{templateCategory(groupTemplate)}</span></div>
                                <Envelope templateId={groupTemplate} />
                                <span className="rp-group-gift-kicker">{t('page.groups.for')}</span>
                                <h3>{groupTitle || t('page.groups.untitled')}</h3>
                                <p>{groupRecipient || t('page.groups.chooseRecipient')}</p>
                                <div className="rp-group-target"><strong>{t('page.balanceAmount', { amount: money(groupTarget || 0) })}</strong><span>{t('page.groups.targetLabel')}</span></div>
                                <div className="rp-preview-facts">
                                    <div className="rp-summary-row"><span>{t('page.groups.contributors')}</span><strong>{groupAudience === 'open' ? t('page.linkTitle') : groupAudienceLabel}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.fundingRule')}</span><strong>{policyLabel(groupPolicy)}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.groups.openFor')}</span><strong>{expiryLabel(groupDeadline)}</strong></div>
                                </div>
                            </section>
                        </aside>
                    </div>
                ) : null}

                {tab === 'groups' && GROUP_GIFT_ENABLED && groupCreated ? (
                    <GroupWorkspace
                        title={groupTitle}
                        recipient={groupRecipient}
                        policy={groupPolicy}
                        deadline={groupDeadline}
                        status={groupStatus}
                        target={groupTarget}
                        funded={funded}
                        contributions={contributions}
                        onCopy={() => {
                            const url = new URL('/red-packet', window.location.origin);
                            url.searchParams.set('groupGift', 'demo');
                            void copyText(url.toString(), t('page.groupCopied'));
                        }}
                        onContribute={() => {
                            const sample = CONTRIBUTION_SAMPLES[contributions.length];
                            const remaining = Math.max(0, groupTarget - funded);
                            if (!sample || remaining <= 0) {
                                message.info(t('page.allReserved'));
                                return;
                            }
                            const nextAmount = Math.min(sample.amount, remaining);
                            const next = [...contributions, { ...sample, amount: nextAmount }];
                            setContributions(next);
                            if (next.reduce((sum, item) => sum + item.amount, 0) >= groupTarget) setGroupStatus('Goal reached');
                            message.success(t('page.reservedBy', { name: sample.name, amount: money(nextAmount) }));
                        }}
                        onCloseFunding={() => {
                            if (groupPolicy === 'All or nothing' && funded < groupTarget) {
                                message.info(t('page.targetLeft', { amount: money(groupTarget - funded) }));
                                return;
                            }
                            setGroupStatus('Settling');
                            message.success(t('page.fundingClosed'));
                        }}
                        onSettle={() => {
                            setGroupStatus('Completed');
                            message.success(t('page.deliveredAmount', { amount: money(funded) }));
                        }}
                        onNew={() => {
                            setGroupCreated(false);
                            setGroupStatus('Draft');
                            setContributions([]);
                        }}
                    />
                ) : null}

                {tab === 'receive' ? (
                    <RecipientPreview
                        packet={receivePacket}
                        packets={receivePackets}
                        ownedPacketIds={packets.map((item) => item.id)}
                        loading={receiveLoading}
                        status={inboxStatus}
                        onSelectStatus={(status) => void loadInbox(status)}
                        onSelectPacket={(packet) => {
                            setLinkPacket(packet);
                            setSearchParams({});
                        }}
                        onClaim={claimOpenedPacket}
                        onClose={() => {
                            void refreshProfile();
                            setSearchParams({});
                            setLinkPacket(null);
                            setTab('create');
                        }}
                        onError={(claimError) => message.error(getApiErrorMessage(claimError, t('page.errors.amount')))}
                    />
                ) : null}
            </div>

            {dialog ? (
                <div className="rp-dialog-backdrop" onClick={closeDialog}>
                    <div className="rp-dialog" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
                        {dialog.kind === 'review' ? (
                            <>
                                <div className="rp-flow-head">
                                    <div>
                                        <h2>{t('page.reviewTitle')}</h2>
                                        <p>{t('page.reviewBody')}</p>
                                    </div>
                                    <button className="rp-dialog-close" type="button" aria-label={t('page.close')} onClick={closeDialog}>×</button>
                                </div>
                                <div className="rp-flow-summary">
                                    <div className="rp-summary-row"><span>{t('page.amount')}</span><strong>{t('page.balanceAmount', { amount: money(amount) })}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.audience')}</span><strong>{audienceLabel}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.recipients')}</span><strong>{recipients}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.distribution')}</span><strong>{modeLabel(mode)}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.template')}</span><strong>{templateName(template)}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.expires')}</span><strong>{expiryLabel(expiry)}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.fee')}</span><strong>{money(fee)}</strong></div>
                                    <div className="rp-summary-row is-receive"><span>{t('page.reserve')}</span><strong>{money(debit)}</strong></div>
                                </div>
                                <div className="rp-flow-form">
                                    <button className="rp-primary is-full" type="button" disabled={submitting} onClick={() => void confirmPacket()}>{submitting ? t('page.creating') : t('page.create')}</button>
                                    <button className="rp-secondary is-full" type="button" onClick={closeDialog}>{t('page.back')}</button>
                                </div>
                            </>
                        ) : null}
                        {dialog.kind === 'ready' ? (
                            <div className="rp-flow-success">
                                <div className="rp-flow-head" style={{ width: '100%' }}>
                                    <div />
                                    <button className="rp-dialog-close" type="button" aria-label={t('page.close')} onClick={closeDialog}>×</button>
                                </div>
                                <div className="rp-success-mark">✓</div>
                                <h2>{t('page.readyTitle')}</h2>
                                <p>{t('page.readyBody', { amount: money(amount), count: recipients, audience: audienceLabel })}</p>
                                <div className="rp-share-row">
                                    <input aria-label={t('page.shareLink')} readOnly value={dialog.url} />
                                    <button className="rp-secondary" type="button" onClick={() => void copyText(dialog.url, t('page.copied'))}>{t('page.copy')}</button>
                                </div>
                                <button className="rp-primary is-full" type="button" onClick={() => { const nextToken = new URL(dialog.url).searchParams.get('token'); if (nextToken) setSearchParams({ token: nextToken }); setTab('receive'); closeDialog(); }}>{t('page.previewRecipient')}</button>
                            </div>
                        ) : null}
                        {dialog.kind === 'group-review' ? (
                            <>
                                <div className="rp-flow-head">
                                    <div>
                                        <h2>{t('page.groupReviewTitle')}</h2>
                                        <p>{t('page.groupReviewBody')}</p>
                                    </div>
                                    <button className="rp-dialog-close" type="button" aria-label={t('page.close')} onClick={closeDialog}>×</button>
                                </div>
                                <div className="rp-flow-summary">
                                    <div className="rp-summary-row"><span>{t('page.gift')}</span><strong>{groupTitle}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.recipient')}</span><strong>{groupRecipient}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.groups.target')}</span><strong>{t('page.balanceAmount', { amount: money(groupTarget) })}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.groups.contributors')}</span><strong>{groupAudienceLabel}</strong></div>
                                    <div className="rp-summary-row"><span>{t('page.fundingRule')}</span><strong>{policyLabel(groupPolicy)}</strong></div>
                                    <div className="rp-summary-row is-receive"><span>{t('page.deadline')}</span><strong>{expiryLabel(groupDeadline)}</strong></div>
                                </div>
                                <div className="rp-flow-form">
                                    <button className="rp-primary is-full" type="button" onClick={() => { setGroupCreated(true); setGroupStatus('Funding'); setContributions([]); closeDialog(); message.success(t('page.published')); }}>{t('page.publish')}</button>
                                    <button className="rp-secondary is-full" type="button" onClick={closeDialog}>{t('page.back')}</button>
                                </div>
                            </>
                        ) : null}
                        {dialog.kind === 'new-circle' ? (
                            <form onSubmit={(event) => {
                                event.preventDefault();
                                const name = circleName.trim();
                                if (!name || submitting || pickedPeople.length === 0) return;
                                setSubmitting(true);
                                void AppApc.CreateCircle({
                                    name,
                                    isShared: circlePrivacy === 'shared',
                                    memberUserIds: pickedPeople.map((person) => person.id),
                                }).then(async () => {
                                    await loadCircles();
                                    closeDialog();
                                    message.success(t('page.created', { name }));
                                }).catch((createError) => {
                                    message.error(getApiErrorMessage(createError, t('page.errors.load')));
                                }).finally(() => setSubmitting(false));
                            }}>
                                <div className="rp-flow-head">
                                    <div>
                                        <h2>{t('page.createCircle')}</h2>
                                        <p>{t('page.createCircleBody')}</p>
                                    </div>
                                    <button className="rp-dialog-close" type="button" aria-label={t('page.close')} onClick={closeDialog}>×</button>
                                </div>
                                <div className="rp-flow-form">
                                    <div className="rp-flow-field">
                                        <label htmlFor="rp-circle-name">{t('page.circleName')}</label>
                                        <input id="rp-circle-name" maxLength={80} placeholder={t('page.circleNamePlaceholder')} required value={circleName} onChange={(event) => setCircleName(event.target.value)} />
                                    </div>
                                    <div className="rp-flow-field">
                                        <label htmlFor="rp-circle-privacy">{t('page.circleType')}</label>
                                        <PacketSelect
                                            id="rp-circle-privacy"
                                            value={circlePrivacy}
                                            onChange={(value) => setCirclePrivacy(value as Privacy)}
                                            options={[
                                                { value: 'shared', label: t('page.sharedOption') },
                                                { value: 'private', label: t('page.privateOption') },
                                            ]}
                                        />
                                    </div>
                                    <PeopleField
                                        label={t('page.addPeople')}
                                        query={peopleQuery}
                                        results={people}
                                        picked={pickedPeople}
                                        onQuery={setPeopleQuery}
                                        onPick={(person) => setPickedPeople((current) => current.some((item) => item.id === person.id) ? current : [...current, person])}
                                        onRemove={(id) => setPickedPeople((current) => current.filter((item) => item.id !== id))}
                                        showEmpty={peopleReady}
                                    />
                                    <button className="rp-primary is-full" type="submit" disabled={submitting || pickedPeople.length === 0}>{submitting ? t('page.creating') : t('page.circles.create')}</button>
                                </div>
                            </form>
                        ) : null}
                        {dialog.kind === 'invite' ? (
                            <form onSubmit={(event) => {
                                event.preventDefault();
                                const person = pickedPeople[0];
                                const invited = circles.find((item) => item.id === dialog.circleId);
                                if (!person || !invited || submitting) return;
                                setSubmitting(true);
                                const request = invited.privacy === 'shared'
                                    ? AppApc.InviteCircleMember({ circleId: invited.id, userId: person.id })
                                    : AppApc.AddCircleMember({ circleId: invited.id, userId: person.id });
                                void request.then(async (response) => {
                                    setCircles((current) => current.map((item) => item.id === invited.id ? toCircle(response.data.data) : item));
                                    closeDialog();
                                    message.success(invited.privacy === 'shared' ? t('page.addedPending') : t('page.addedPrivate'));
                                }).catch((inviteError) => {
                                    message.error(getApiErrorMessage(inviteError, t('page.errors.load')));
                                }).finally(() => setSubmitting(false));
                            }}>
                                <div className="rp-flow-head">
                                    <div>
                                        <h2>{t(circles.find((item) => item.id === dialog.circleId)?.privacy === 'shared' ? 'page.inviteTo' : 'page.addTo', { name: circles.find((item) => item.id === dialog.circleId)?.name })}</h2>
                                        <p>{circles.find((item) => item.id === dialog.circleId)?.privacy === 'shared' ? t('page.invitePendingBody') : t('page.invitePrivateBody')}</p>
                                    </div>
                                    <button className="rp-dialog-close" type="button" aria-label={t('page.close')} onClick={closeDialog}>×</button>
                                </div>
                                <div className="rp-flow-form">
                                    <PeopleField
                                        label={t('page.searchPeople')}
                                        query={peopleQuery}
                                        results={people}
                                        picked={pickedPeople.slice(0, 1)}
                                        onQuery={setPeopleQuery}
                                        onPick={(person) => setPickedPeople([person])}
                                        onRemove={() => setPickedPeople([])}
                                        showEmpty={peopleReady}
                                    />
                                    <button className="rp-primary is-full" type="submit" disabled={submitting || pickedPeople.length === 0}>{circles.find((item) => item.id === dialog.circleId)?.privacy === 'shared' ? t('page.addInvite') : t('page.circles.addPerson')}</button>
                                </div>
                            </form>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function GroupWorkspace({
    title,
    recipient,
    policy,
    deadline,
    status,
    target,
    funded,
    contributions,
    onCopy,
    onContribute,
    onCloseFunding,
    onSettle,
    onNew,
}: {
    title: string;
    recipient: string;
    policy: string;
    deadline: string;
    status: GroupStatus;
    target: number;
    funded: number;
    contributions: Contribution[];
    onCopy: () => void;
    onContribute: () => void;
    onCloseFunding: () => void;
    onSettle: () => void;
    onNew: () => void;
}) {
    const { t } = useTranslation('redPacket');
    const progress = Math.min(100, Math.round((funded / target) * 100));
    const remaining = Math.max(0, target - funded);
    const statusClass = status === 'Goal reached' || status === 'Completed' ? ' is-done' : status === 'Settling' ? ' is-settling' : '';
    const contributionStatus = status === 'Completed'
        ? t('page.workspace.delivered')
        : status === 'Settling'
            ? t('page.workspace.pending')
            : t('page.workspace.reserved');
    const progressMessage = status === 'Completed'
        ? t('page.workspace.deliveredMsg')
        : status === 'Settling'
            ? t('page.workspace.settlingMsg')
            : remaining > 0
                ? t('page.workspace.remaining', { amount: money(remaining) })
                : t('page.workspace.readyClose');
    const fundingOpen = status === 'Funding' || status === 'Goal reached';

    return (
        <>
            <section className="rp-feature-intro">
                <div>
                    <span className="rp-feature-kicker">{t('page.workspace.kicker')}</span>
                    <h2>{title}</h2>
                    <p>{t('page.workspace.for', { recipient, policy: t(`page.policies.${policy}`, { defaultValue: policy }), deadline: t(`open.expiry.${deadline}`, { defaultValue: deadline }) })}</p>
                </div>
                <span className={`rp-gift-status${statusClass}`}>{t(`page.groupStatus.${status}`, { defaultValue: status })}</span>
            </section>
            <div className="rp-group-workspace">
                <section className="rp-panel rp-group-progress-panel">
                    <div className="rp-group-progress-head">
                        <div>
                            <small>{t('page.workspace.funded')}</small>
                            <strong>{money(funded)} <span>{t('page.workspace.of', { amount: money(target) })}</span></strong>
                        </div>
                        <span>{progress}%</span>
                    </div>
                    <div className="rp-group-progress" role="progressbar" aria-label={t('page.workspace.funding')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
                        <i style={{ width: `${progress}%` }} />
                    </div>
                    <div className="rp-contribution-rail" aria-hidden>
                        {[0, 1, 2].map((index) => <i key={index} className={index < contributions.length ? 'is-filled' : ''} />)}
                    </div>
                    <p>{progressMessage}</p>
                    <div className="rp-group-actions">
                        <button className="rp-secondary" type="button" onClick={onCopy}>{t('page.workspace.copy')}</button>
                        {fundingOpen ? (
                            <>
                                <button className="rp-secondary" type="button" disabled={remaining === 0} onClick={onContribute}>{t('page.workspace.contribute')}</button>
                                <button className="rp-primary" type="button" disabled={funded === 0} onClick={onCloseFunding}>{t('page.workspace.closeFunding')}</button>
                            </>
                        ) : null}
                        {status === 'Settling' ? <button className="rp-primary" type="button" onClick={onSettle}>{t('page.workspace.settle')}</button> : null}
                        <button className="rp-text-button" type="button" onClick={onNew}>{t('page.workspace.another')}</button>
                    </div>
                </section>
                <section className="rp-panel rp-contribution-panel">
                    <div className="rp-list-head">
                        <div>
                            <h3>{t('page.workspace.contributions')}</h3>
                            <p>{t('page.workspace.each')}</p>
                        </div>
                        <span>{t('page.workspace.people', { count: contributions.length })}</span>
                    </div>
                    {contributions.length ? (
                        <div>
                            {contributions.map((item) => (
                                <div className="rp-contribution-row" key={item.name}>
                                    <span className="rp-member-avatar">{item.initials}</span>
                                    <div><strong>{item.name}</strong><small>{contributionStatus}</small></div>
                                    <b>{money(item.amount)}</b>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <RedPacketEmpty title={t('page.workspace.emptyTitle')} description={t('page.workspace.emptyBody')} />
                    )}
                </section>
            </div>
            <div className="rp-principle">
                <span>↗</span>
                <div>
                    <strong>{t('page.workspace.principleTitle')}</strong>
                    <p>{t('page.workspace.principleBody')}</p>
                </div>
            </div>
        </>
    );
}

function PeopleField({
    label,
    query,
    results,
    picked,
    onQuery,
    onPick,
    onRemove,
    showEmpty = false,
}: {
    label: string;
    query: string;
    results: CirclePerson[];
    picked: CirclePerson[];
    onQuery: (value: string) => void;
    onPick: (person: CirclePerson) => void;
    onRemove: (id: string) => void;
    showEmpty?: boolean;
}) {
    const { t } = useTranslation('redPacket');
    const pickedIds = new Set(picked.map((person) => person.id));
    const people = [
        ...picked.filter((person) => !results.some((item) => item.id === person.id)),
        ...results,
    ];
    return (
        <div className="rp-flow-field">
            <label htmlFor="rp-people-search">{label}</label>
            <input id="rp-people-search" value={query} placeholder={t('page.searchPeople')} onChange={(event) => onQuery(event.target.value)} />
            {showEmpty && query.trim().length >= 2 && results.length === 0 && picked.length === 0 ? <RedPacketEmpty description={t('page.noPeople')} /> : null}
            <div className="rp-people-results">
                {people.map((person) => {
                    const selected = pickedIds.has(person.id);
                    return (
                        <button
                            key={person.id}
                            type="button"
                            className={selected ? 'is-selected' : ''}
                            aria-pressed={selected}
                            onClick={() => (selected ? onRemove(person.id) : onPick(person))}
                        >
                            <span>{person.displayName} · {person.email}</span>
                            {selected ? <CheckOutlined /> : null}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function RecipientPreview({
    packet,
    packets,
    ownedPacketIds,
    loading,
    status,
    onSelectStatus,
    onSelectPacket,
    onClaim,
    onClose,
    onError,
}: {
    packet: RedPacket | null;
    packets: RedPacket[];
    ownedPacketIds: string[];
    loading: boolean;
    status: RedPacketListStatus;
    onSelectStatus: (status: RedPacketListStatus) => void;
    onSelectPacket: (packet: RedPacket) => void;
    onClaim: (packet: RedPacket) => Promise<RedPacket>;
    onClose: () => void;
    onError: (error: unknown) => void;
}) {
    const { t } = useTranslation('redPacket');
    const { user } = useAuth();
    const [phase, setPhase] = useState<'sealed' | 'opening' | 'opened'>('sealed');
    const [claimLoading, setClaimLoading] = useState(false);
    const claimPendingRef = useRef(false);
    const claimTimerRef = useRef<number | null>(null);
    const tabs = [
        ['openable', t('open.tabs.openable'), 'openable'],
        ['expired', t('open.tabs.expired'), 'expired'],
        ['claimed', t('open.tabs.claimed'), 'fully_claimed'],
    ] as const;
    const templateId = API_TO_TEMPLATE[packet?.template ?? 'CLASSIC'] ?? 'classic';
    const sender = packet?.creatorDisplayName || 'APC';
    const viewerEmail = user?.email?.trim().toLowerCase();
    const createdByViewer = Boolean(
        packet && (
            ownedPacketIds.includes(packet.id)
            || (user?.id && packet.creatorUserId === user.id)
            || (viewerEmail && packet.creatorEmail?.trim().toLowerCase() === viewerEmail)
        ),
    );
    const claim = packet?.myClaim;
    const opened = claim?.status === 'CREDITED' || claim?.status === 'ALLOCATED' || claim?.status === 'PAYOUT_SENT';
    const view = status === 'expired' || packet?.status === 'EXPIRED'
        ? 'expired'
        : status === 'fully_claimed' || (packet?.status === 'FULLY_CLAIMED' && !claim)
            ? 'claimed'
            : 'openable';

    useEffect(() => {
        setPhase(opened ? 'opened' : 'sealed');
        setClaimLoading(false);
        claimPendingRef.current = false;
        if (claimTimerRef.current) {
            window.clearTimeout(claimTimerRef.current);
            claimTimerRef.current = null;
        }
    }, [packet?.id, opened]);

    const openPacket = () => {
        if (!packet?.canClaim || phase !== 'sealed') return;
        setPhase('opening');
        setClaimLoading(false);
        claimPendingRef.current = true;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const animationMs = reduceMotion ? 180 : 1680;
        const wait = new Promise((resolve) => {
            claimTimerRef.current = window.setTimeout(() => {
                claimTimerRef.current = null;
                if (claimPendingRef.current) setClaimLoading(true);
                resolve(undefined);
            }, animationMs);
        });
        void Promise.all([onClaim(packet), wait]).then(() => {
            claimPendingRef.current = false;
            setClaimLoading(false);
            setPhase('opened');
        }).catch((error) => {
            claimPendingRef.current = false;
            if (claimTimerRef.current) {
                window.clearTimeout(claimTimerRef.current);
                claimTimerRef.current = null;
            }
            setClaimLoading(false);
            setPhase('sealed');
            onError(error);
        });
    };

    return (
        <div className="rp-recipient">
            <div className="rp-scenarios" role="tablist" aria-label={t('open.tabsLabel')}>
                {tabs.map(([id, label, query]) => (
                    <button
                        key={id}
                        type="button"
                        role="tab"
                        className={status === query ? 'is-active' : ''}
                        aria-selected={status === query}
                        onClick={() => onSelectStatus(query)}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {!loading && packets.length > 1 ? (
                <PacketSelect
                    ariaLabel={t('open.tabsLabel')}
                    value={packet?.id || packets[0].id}
                    onChange={(id) => {
                        const next = packets.find((item) => item.id === id);
                        if (next) onSelectPacket(next);
                    }}
                    options={packets.map((item) => ({
                        value: item.id,
                        label: item.message || item.creatorDisplayName,
                    }))}
                />
            ) : null}
            <section className={`rp-panel rp-recipient-card rp-open-stage${loading ? ' is-loading' : ''}${!loading && !packet ? ' is-empty' : ''}${!loading && view !== 'openable' ? ' is-static' : ''}${!loading && view === 'openable' && phase === 'opening' ? ' is-opening' : ''}${!loading && view === 'openable' && phase === 'opened' ? ' is-opened' : ''}`} aria-busy={loading}>
                <Fireworks active={!loading && Boolean(packet) && view === 'openable' && phase !== 'sealed'} />
                {loading ? (
                    <div className="rp-recipient-loading" role="status" aria-label={t('page.loading')}>
                        <span className="rp-envelope-spinner" aria-hidden />
                    </div>
                ) : null}
                {!loading && !packet ? <RedPacketEmpty description={t('page.emptyInbox')} /> : null}
                {!loading && packet && (view === 'expired' || view === 'claimed') ? (
                    <>
                        <Envelope templateId={templateId} />
                        <h2>{view === 'expired' ? t('open.expiredTitle') : t('open.claimedTitle')}</h2>
                        <p className="rp-lead">{view === 'expired' ? t('open.expiredBody', { name: sender }) : t('open.claimedBody')}</p>
                    </>
                ) : null}
                {!loading && packet && view === 'openable' && phase === 'opened' ? (
                    <div className="rp-open-reveal">
                        <div className="rp-success-mark">✓</div>
                        <h2>{claim?.status === 'CREDITED' ? t('open.receivedTitle') : t('page.claimPending')}</h2>
                        <div className="rp-result">+{money(Number(claim?.amount || 0))} <small>{t('open.unit')}</small></div>
                        <p className="rp-lead">{claim?.status === 'CREDITED' ? t('open.receivedHint', { name: sender }) : t('page.claimPending')}</p>
                        <div className="rp-open-actions">
                            <button className="rp-secondary" type="button" onClick={onClose}>{t('open.close')}</button>
                        </div>
                    </div>
                ) : null}
                {!loading && packet && view === 'openable' && phase !== 'opened' ? (
                    <>
                        {createdByViewer ? null : <small>{t('open.from', { name: sender })}</small>}
                        <Envelope templateId={templateId} opening={phase === 'opening'} loading={claimLoading} />
                        <div className="rp-open-copy">
                            <h2>{packet.message}</h2>
                            <p className="rp-lead">
                                {t('open.meta', {
                                    count: packet.receiverCount,
                                    mode: t(`open.modes.${packet.split === 'EQUAL' ? 'Equal' : 'Random'}`),
                                    expiry: formatDateTime(packet.expiresAt),
                                })}
                            </p>
                            {packet.canClaim ? (
                                <button className="rp-primary is-full" type="button" disabled={phase === 'opening'} onClick={openPacket}>
                                    {phase === 'opening' ? t('open.opening') : t('open.action')}
                                </button>
                            ) : (
                                <p className="rp-hint">{t('page.cannotClaim')}</p>
                            )}
                        </div>
                    </>
                ) : null}
            </section>
        </div>
    );
}

export default RedPacketPage;
